import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { Stand } from "@/shared/models/Stand";
import { ErrorModal } from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { isNil } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { getStandsByMillAdjustment } from "../repositories/AdjustmentManagerRepository";

interface Props {
	standsOfMillAdjustment: Stand[];
	onSelectionChanged: Function;
	limitSelectableStands?: String[];
}

type useStandsByMillAdjustmentProps = {
	millAdjustmentId?: number;
	onError?: (error: Error) => void;
	onSuccess?: (data: Stand[]) => void;
};

export const useStandsByMillAdjustment = ({
	millAdjustmentId,
	onError,
	onSuccess,
}: useStandsByMillAdjustmentProps) => {
	return useQuery<Stand[], Error>({
		queryKey: ["standsByMillAdjustment", millAdjustmentId],
		queryFn: ({ signal }) =>
			getStandsByMillAdjustment(
				{ millAdjustmentId: millAdjustmentId ?? null },
				signal,
			),
		onError: onError,
		onSuccess: onSuccess,
	});
};

export const StandsOfMillAdjustmentSelector = ({
	standsOfMillAdjustment,
	onSelectionChanged,
	limitSelectableStands,
}: Props) => {
	const [allStands, setAllStands] = useState<Stand[]>([]);
	const [error, setError] = useState<string>("");

	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { data: stands, isLoading } = useStandsByMillAdjustment({
		onError: (error) => setError(error.message),
		onSuccess: (data) => {
			if (data && data.length > 0) {
				setAllStands([...data]);
			}
		},
	});

	const rows = useMemo(() => {
		return allStands
			.sort((a, b) => (a.StandName < b.StandName ? -1 : 1))
			.map(({ StandId, StandName }) => {
				return {
					StandId: StandId,
					StandName: StandName,
				};
			});
	}, [allStands]);

	const columns: ColDef[] = [
		{
			field: "StandName",
			headerName: "Stand",
			flex: 2,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.StandName?.localeCompare(nodeB.data.StandName);
				}
			},
		},
	];

	const { defaultColDef } = useGridDefinitions({});

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
		params.api.forEachNode((node) => {
			node.setSelected(
				!!node.data &&
					!isNil(
						standsOfMillAdjustment.find((p) => p.StandId === node.data.StandId),
					),
			);
		});
	};

	const onGridReady = useCallback(
		(event: GridReadyEvent) => {
			setGridApi(event.api);
		},
		[setGridApi],
	);

	const getRowStyle = (params: any) => {
		if (
			limitSelectableStands &&
			!limitSelectableStands.includes(params.data?.StandName)
		) {
			return {
				color: "#999999",
			};
		}
		return undefined;
	};

	return (
		<>
			<div
				style={{
					width: "100%",
					height: "37vh",
				}}
			>
				<AgGridReact<Stand>
					loading={isLoading}
					getRowId={(params) => `${params.data.StandId}`}
					onGridReady={onGridReady}
					rowHeight={25}
					headerHeight={25}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					animateRows
					onSelectionChanged={onAgGridSelectionChanged}
					gridOptions={{
						rowSelection: {
							mode: "multiRow",
							checkboxes: true,
							enableSelectionWithoutKeys: true,
							enableClickSelection: true,
							headerCheckbox: false,
							isRowSelectable: (node) => {
								if (!node.data?.StandName) return false;
								if (
									!limitSelectableStands ||
									limitSelectableStands.length === 0
								)
									return true;

								return limitSelectableStands.includes(node.data.StandName);
							},
						},
					}}
					getRowStyle={getRowStyle}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
